/**
 * Live MCP `tools/list` POST helper — the strongest single fact Sill can
 * point to on the Claude connectors path (ADR-0159 §5).
 *
 * Envelope, audited in ADR-0159 §5:
 *   - URL:     the merchant's live MCP endpoint (`buildMcpUrl(site_key_hash)`)
 *   - Method:  POST only (CORS-open `*` per ADR-0014 §1.6)
 *   - Body:    the FROZEN module-scope constant {@link MCP_TOOLS_LIST_BODY}
 *              — never templated, never composed at call time
 *   - Headers: `Content-Type: application/json`, `Accept: application/json`
 *   - Creds:   none (no cookies, no bearer)
 *   - Timeout: browser default; failure yields the standard
 *              "(couldn't reach the surface: …)" copy
 *   - Read-only invariant: `tools/list` under ADR-0014 §1.4 is discovery-
 *              only. The wrapper never invokes any merchant backend.
 *
 * One-shot gating (installed + protocol + `mcpCalledForThisRun`) is the
 * caller's responsibility — the helper here is unconditional.
 *
 * Response handling reuses the `fetchSurface` trim pattern (900-char cap,
 * JSON.parse try/catch, non-JSON preserved as raw). No mutation, no state
 * leaves the view.
 */

import type { LiveResult } from './fetch-surface.js';

/**
 * The FROZEN JSON-RPC 2.0 body. Module-scope constant — a security
 * reviewer can audit it by grep, without reading a builder. Not exported
 * as anything other than a `string` constant so the caller cannot mutate
 * the payload shape at run time.
 *
 * Byte-equal assertion in the test file pins the shape.
 */
export const MCP_TOOLS_LIST_BODY =
  '{"jsonrpc":"2.0","id":1,"method":"tools/list","params":{}}';

/**
 * Fire the one-shot MCP `tools/list` call. Returns a {@link LiveResult}
 * identical in shape to the discovery-surface helper — the endpoint
 * column rendering is uniform across all LIVE tiers.
 *
 * Never throws: transport failures resolve to a `status: 0` result with
 * the "couldn't reach the surface" body, matching {@link fetchSurface}.
 */
export async function callToolsList(url: string): Promise<LiveResult> {
  const t0 =
    typeof performance !== 'undefined' ? performance.now() : Date.now();
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: MCP_TOOLS_LIST_BODY,
      // No cookies, no bearer — the endpoint is CORS-open (ADR-0014
      // §1.6) but Sill's session-cookie policy would attach the app's
      // cookie if we let it. `credentials: 'omit'` is the honest posture.
      credentials: 'omit',
    });
    const text = await res.text();
    const ms = Math.round(
      (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0,
    );
    let body = text;
    let json: unknown;
    try {
      json = JSON.parse(text);
      body = JSON.stringify(json, null, 2);
    } catch {
      /* non-JSON or partial — show raw */
    }
    if (body.length > 900) body = `${body.slice(0, 900)}\n… (truncated)`;
    return {
      status: res.status,
      ms,
      body,
      ok: res.ok,
      ...(json !== undefined ? { json } : {}),
    };
  } catch (err) {
    const ms = Math.round(
      (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0,
    );
    return {
      status: 0,
      ms,
      body: `(couldn't reach the surface: ${
        err instanceof Error ? err.message : 'network error'
      })`,
      ok: false,
    };
  }
}
